import React, {useMemo, useState} from 'react';
import {useSelector} from 'react-redux';

import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {activeNotificationsSelector} from 'src/modules/internal-notification';
import {useNotificationTopics} from 'src/modules/internal-notification/hook/useNotificationTopics';
import {SortDirection} from 'src/openapi/admin/models/SortDirection';


import {userNotificationsSidebar} from '../user-notifications.module.styl';

import {Filters} from './components/filters';
import {Topics} from './components/topics';

import type {Notification} from 'src/openapi/admin/models/Notification';

type Props = {
    notifications: Notification[],
};

export const UserNotificationsSidebar = ({notifications}: Props) => {
    const {
        isLoading: isTopicsLoading,
        loadingError: topicsLoadingError,
        data: topics,
    } = useNotificationTopics();

    const notificationsResponse = useSelector(activeNotificationsSelector);

    const [titleFilter, setTitleFilter] = useState<string>('');
    const [dateSortDirection, setDateSortDirection] = useState<SortDirection>(SortDirection.ASC);
    const [activeOnly, setActiveOnly] = useState<boolean>(false);

    const filteredNotifications = useMemo(() => (
        notifications
            .filter(notification => notification.title.toLowerCase().includes(titleFilter.toLowerCase()))
            .filter(notification => (activeOnly && notificationsResponse?.data)
                ? notificationsResponse.data.some(item => item.notificationId === notification.notificationId)
                : true)
    ), [notifications, titleFilter, activeOnly, notificationsResponse]);

    const userTopics = useMemo(() => (
        topics.filter(topic => filteredNotifications.some(notification => notification.topic === topic))
    ), [topics, filteredNotifications]);

    return (
        <Skeleton shown={isTopicsLoading} view={<div/>}>
            <ServerResponseHandler serverError={topicsLoadingError} smallView={true}>
                <div className={userNotificationsSidebar}>
                    <Filters
                        isActiveOnly={activeOnly}
                        onActiveOnlyToggle={setActiveOnly}
                        titleFilter={titleFilter}
                        onTitleFilterChange={setTitleFilter}
                        dateSortDirection={dateSortDirection}
                        onSort={setDateSortDirection}
                    />
                    <Topics
                        userTopics={userTopics}
                        notifications={filteredNotifications}
                        dateSortDirection={dateSortDirection}
                    />
                </div>
            </ServerResponseHandler>
        </Skeleton>
    );
};
